import React, { useEffect, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, Popover, Typography, useMediaQuery, useTheme } from '@mui/material'
import { bindTrigger, bindPopover } from 'material-ui-popup-state'
import { StyledContainerColumnLeft } from '../../styles/ContainerStyles'
import { RequestProps } from '../../store/types'
import { useTranslation } from 'react-i18next'
import { usePopupState } from 'material-ui-popup-state/hooks'
import { FilterList } from '@mui/icons-material'
import { Option } from '../Form/Inputs/SelectInput'
import { useLeftPadding } from '../../store/redux/hooks'

type SingleFilterPopupProps = {
  singleFilterButtonText: string
  singleFilterHeaderText: string
  popupId: string
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  dynamicPropName: keyof RequestProps
  optionsToChoose: Option[] | undefined
  notFoundText: string
}

const SingleFilterPopup = ({
  setRequestProps,
  singleFilterButtonText,
  singleFilterHeaderText,
  popupId,
  dynamicPropName,
  optionsToChoose,
  notFoundText,
}: SingleFilterPopupProps) => {
  const { t } = useTranslation()
  const popupState = usePopupState({ variant: 'popover', popupId: popupId })
  const isDownSm = useMediaQuery(useTheme().breakpoints.down('sm'))
  const leftPadding = useLeftPadding()
  
  // FILTER BUTTON
  const [isActive, setIsActive] = useState(false)

  // SELECTED
  const [selected, setSelected] = useState<string[]>([])

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, option: string) => {
    e.target.checked
      ? setSelected(prev => [...prev, option])
      : setSelected(prev => prev.filter(item => item !== option))
  }

  // CLEAR FILTERS
  const handleClear = () => {
    setSelected([])
  }

  useEffect(() => {
    setIsActive(selected.length > 0)

    setRequestProps(prev => {
      return selected.length > 0 ? { ...prev, [dynamicPropName]: selected } : { ...prev, [dynamicPropName]: undefined }
    })
  }, [selected, setRequestProps, dynamicPropName])

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={isActive ? 'selected' : ''}
        {...bindTrigger(popupState)}
        aria-label={singleFilterButtonText}
      >
        <FilterList />
        {!isDownSm && <Typography sx={{ px: 1 }}>{singleFilterButtonText}</Typography>}
      </Button>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: -10,
          horizontal: 'center',
        }}
      >
        <StyledContainerColumnLeft sx={{ pr: 3, py: 2, gap: 1 }}>
          <Typography
            sx={{ pl: leftPadding }}
            variant="h5"
            component="span"
            alignItems="center"
            color="text.highEmphasis"
          >
            {t('page.adminsPage.filtering.filters')}
          </Typography>
          <Typography
            sx={{ pl: leftPadding }}
            variant="h6"
            component="span"
            color="text.mediumEmphasis"
          >
            {singleFilterHeaderText}
          </Typography>
          <Box
            sx={{
              pl: leftPadding,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              pr: 1,
              mb: 1,
              maxHeight: 200,
              overflowX: 'auto',
            }}
          >
            {optionsToChoose === undefined && <p>{t('message.somethingWentWrong')}</p>}
            {optionsToChoose?.length === 0 ? (
              <p>{notFoundText}</p>
            ) : (
              optionsToChoose?.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox checked={selected.includes(option.value)} onChange={e => handleSelect(e, option.value)} />
                  }
                  label={option.label}
                />
              ))
            )}
          </Box>
          <Button 
            sx={{ ml: leftPadding }}
            onClick={handleClear} 
            variant="text" 
            color="primary" 
            size="small"
          >
            {t('page.adminsPage.filtering.clear')}
          </Button>
        </StyledContainerColumnLeft>
      </Popover>
    </>
  )
}

export default SingleFilterPopup
